import React, { useEffect, useState } from "react";
import { FaEye } from "react-icons/fa";
import { useTheme } from "../../ThemeContext";
import { getAllDocs } from "../../api/api";
import DocVersions from "./DocVersions";
import ViewFileType from "../ViewFileType";

const AllFiles = ({ project_id }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [selectedExtension, setSelectedExtension] = useState("all");
  const [files, setFiles] = useState([]);
  const [groupedFiles, setGroupedFiles] = useState({});
  const [isExpanded, setIsExpanded] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedVersionFileName, setSelectedVersionFileName] = useState("");
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);

  const fetchAndOrganizeFiles = async () => {
    try {
      const res = await getAllDocs({ projectId: project_id });
      const allFiles = res.data.files || [];

      const filesWithExtensions = allFiles.map((file) => ({
        ...file,
        extension: file.orignalFileName?.split(".").pop()?.toLowerCase() || "",
      }));

      const grouped = filesWithExtensions.reduce((acc, file) => {
        if (!file.extension) return acc;
        if (!acc[file.extension]) acc[file.extension] = [];
        acc[file.extension].push(file);
        return acc;
      }, {});

      setFiles(filesWithExtensions);
      setGroupedFiles(grouped);
    } catch (error) {
      console.error("Failed to load documents:", error);
    }
  };

  useEffect(() => {
    fetchAndOrganizeFiles();
  }, [project_id]);

  const openFilePreview = (file) => {
    setSelectedFile(file);
    setIsViewerOpen(true);
  };

  const closeFileViewer = () => {
    setIsViewerOpen(false);
    setSelectedFile(null);
  };

  const fileList = selectedExtension === "all" ? files : groupedFiles[selectedExtension] || [];

  return (
    <div className="flex min-h-screen">
      {isVersionsOpen ? (
        <DocVersions
          onClose={() => setIsVersionsOpen(false)}
          file={selectedVersionFileName}
          folderPath={selectedFile?.folderPath}
          project_id={project_id}
        />
      ) : (
        <>
          <div className={`md:w-1/5 p-4 ${isDark ? "bg-gray-800" : "bg-gray-50"} rounded-lg mr-8 w-1/4`}>
            <h3 className="text-xl font-bold text-gray-500 mb-4">File Types</h3>
            <ul className="list-none p-0">
              <li
                onClick={() => setSelectedExtension("all")}
                className={`p-2 cursor-pointer rounded-md mb-2 transition-colors ${
                  selectedExtension === "all" ? (isDark ? "bg-gray-700" : "bg-gray-500") : "bg-transparent"
                } ${isDark ? "text-blue-400" : "text-gray-800"}`}
              >
                All Files
              </li>
              {Object.keys(groupedFiles).map((extension) => (
                <li
                  key={extension}
                  onClick={() => setSelectedExtension(extension)}
                  className={`p-2 cursor-pointer rounded-md mb-2 transition-colors ${
                    selectedExtension === extension ? (isDark ? "bg-gray-800" : "bg-gray-400") : "bg-transparent"
                  } ${isDark ? "text-gray-100" : "text-gray-800"}`}
                >
                  <span>{isExpanded ? extension.toUpperCase() : extension.charAt(0).toUpperCase()}</span>
                </li>
              ))}
            </ul>
            <button
              className={`mt-4 px-4 py-2 md:hidden block text-sm rounded-md ${
                isDark ? "bg-gray-600 text-gray-100" : "bg-gray-300 text-black"
              }`}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Show Less" : "Show More"}
            </button>
          </div>

          <div className={`md:w-4/5 ${isDark ? "bg-gray-800" : "bg-gray-50"} rounded-md p-5 w-3/4`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {fileList.map((doc, index) => (
                <div key={index} className="bg-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-all transform hover:scale-105">
                  <h3 className="text-lg mb-2 font-semibold text-gray-800 truncate">{doc.fileName}</h3>

                  <button
                    onClick={() => openFilePreview(doc)}
                    className="bg-blue-600 text-white py-2 px-4 rounded-md font-semibold w-full mb-3"
                  >
                    View {doc.extension?.toUpperCase() || "File"}
                  </button>

                  <button
                    onClick={() => {
                      setSelectedFile(doc);
                      setSelectedVersionFileName(doc.fileName);
                      setIsVersionsOpen(true);
                    }}
                    className="flex items-center justify-center text-blue-500 hover:text-blue-700 mx-auto"
                  >
                    <FaEye className="mr-2" /> View Versions
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <ViewFileType open={isViewerOpen} onClose={closeFileViewer} file={selectedFile} projectId={project_id} />
    </div>
  );
};

export default AllFiles;

